import { useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Promise-based confirm/prompt, replacing `window.confirm` and `window.prompt`.
 *
 * Native dialogs were doing real damage in an operator console:
 *   - They render in browser chrome, so an enterprise buyer sees an unstyled
 *     OS alert stamped with our origin the moment they touch anything
 *     destructive ("Permanently delete ALL resolved conversations?").
 *   - They are modal to the whole *renderer*, not the app: background polling,
 *     transitions, and toasts all freeze until someone clicks.
 *   - Chrome lets a user check "don't show again", after which `confirm()`
 *     silently returns false forever and destructive buttons look broken.
 *   - Nothing about them can carry tone, so a merge and a permanent delete
 *     looked identical.
 *
 * Same shape as the Toaster: a module-level store plus one `<ConfirmHost />`
 * in App, so call sites need no context and no hooks —
 *
 *     if (!(await confirmDialog({ title: "…", tone: "danger" }))) return;
 *
 * If no host is mounted (a unit test rendering a page in isolation), we fall
 * back to the native dialog rather than returning a promise that never settles.
 */

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` styles the confirm button red and focuses Cancel first. */
  tone?: "default" | "danger";
  /**
   * Require the operator to type this exact word before the confirm button
   * works. For an action nobody can undo, one click on a dialog is not consent
   * -- it is the same reflex that dismissed the last six dialogs. Typing is the
   * cheapest way to make the hand stop and the eyes read.
   */
  confirmPhrase?: string;
}

export interface PromptOptions {
  title: string;
  body?: string;
  label: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type Request =
  | { kind: "confirm"; id: number; options: ConfirmOptions; settle: (value: boolean) => void }
  | { kind: "prompt"; id: number; options: PromptOptions; settle: (value: string | null) => void };

let current: Request | null = null;
let nextId = 1;
let hostCount = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function settleCurrent(value: boolean | string | null) {
  const request = current;
  if (!request) return;
  current = null;
  emit();
  // Cast is safe: `value` is produced by the branch that matches `request.kind`.
  (request.settle as (v: boolean | string | null) => void)(value);
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  if (hostCount === 0) {
    const lines = [options.title, options.body].filter(Boolean).join("\n\n");
    if (options.confirmPhrase) {
      // The fallback must not be weaker than the real dialog: an isolated page
      // render with no host still has to type the word.
      const typed = window.prompt(
        lines + "\n\nType " + options.confirmPhrase + " to confirm.",
        "",
      );
      return Promise.resolve(typed?.trim() === options.confirmPhrase);
    }
    return Promise.resolve(window.confirm(lines));
  }
  return new Promise<boolean>((resolve) => {
    // A second request while one is open resolves the first as cancelled, so a
    // stray double-trigger can never leave an orphaned promise pending.
    settleCurrent(false);
    current = { kind: "confirm", id: nextId++, options, settle: resolve };
    emit();
  });
}

export function promptDialog(options: PromptOptions): Promise<string | null> {
  if (hostCount === 0) {
    const lines = [options.title, options.body].filter(Boolean).join("\n\n");
    return Promise.resolve(window.prompt(lines, options.defaultValue ?? ""));
  }
  return new Promise<string | null>((resolve) => {
    settleCurrent(null);
    current = { kind: "prompt", id: nextId++, options, settle: resolve };
    emit();
  });
}

export function ConfirmHost() {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    hostCount += 1;
    listeners.add(force);
    return () => {
      hostCount -= 1;
      listeners.delete(force);
    };
  }, []);

  if (!current) return null;
  // Keyed on the request id so a replaced dialog remounts with fresh state
  // (its own input value, its own initial focus) instead of reusing the old.
  return <ConfirmSurface key={current.id} request={current} />;
}

function ConfirmSurface({ request }: { request: Request }) {
  const [typed, setTyped] = useState("");
  const dialogRef = useRef<HTMLElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const cancelValue = request.kind === "confirm" ? false : null;

  useEffect(() => {
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const backdrop = backdropRef.current;
    const inertStates = [...document.body.children]
      .filter((element): element is HTMLElement => (
        element instanceof HTMLElement && element !== backdrop
      ))
      .map((element) => [element, element.inert] as const);
    for (const [element] of inertStates) element.inert = true;

    if (request.kind === "prompt" || request.options.confirmPhrase) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (request.options.tone === "danger") {
      // Destructive default: land on Cancel, so a stray Enter or Space from the
      // keystroke that opened the dialog can never delete anything.
      cancelRef.current?.focus();
    } else {
      confirmRef.current?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        settleCurrent(cancelValue);
        return;
      }
      if (event.key !== "Tab") return;
      event.stopPropagation();
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      )].filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (
        !dialog.contains(active)
        || (!event.shiftKey && active === last)
        || (event.shiftKey && active === first)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    // Capture phase + stopPropagation: every other surface in the app listens
    // for Escape on `document` in the bubble phase. Without this, dismissing a
    // confirm raised from inside the identity review dialog or the saved-view
    // editor would close that surface too and throw away the operator's work.
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      for (const [element, wasInert] of inertStates) element.inert = wasInert;
      const opener = previousFocusRef.current;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [cancelValue, request]);

  const { options } = request;
  const danger = request.kind === "confirm" && request.options.tone === "danger";
  const phrase = request.kind === "confirm" ? request.options.confirmPhrase : undefined;
  // Exact, after trimming: a phrase that accepts near-misses is decoration.
  const phraseSatisfied = !phrase || typed.trim() === phrase;
  const confirmLabel = options.confirmLabel ?? (danger ? "Delete" : "Confirm");
  const cancelLabel = options.cancelLabel ?? "Cancel";
  const titleId = `confirm-title-${request.id}`;
  const bodyId = options.body ? `confirm-body-${request.id}` : undefined;

  const submit = () => {
    if (request.kind === "prompt") {
      const value = inputRef.current?.value.trim() ?? "";
      if (!value) {
        inputRef.current?.focus();
        return;
      }
      settleCurrent(value);
      return;
    }
    if (!phraseSatisfied) {
      inputRef.current?.focus();
      return;
    }
    settleCurrent(true);
  };

  return createPortal(
    <div
      ref={backdropRef}
      className="modal-scrim confirm-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        settleCurrent(cancelValue);
      }}
    >
      <section
        ref={dialogRef}
        className="panel modal-card confirm-dialog"
        role="alertdialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
      >
        <h2 id={titleId} className="confirm-dialog-title">{options.title}</h2>
        {options.body ? (
          <p id={bodyId} className="confirm-dialog-body">{options.body}</p>
        ) : null}
        {phrase ? (
          <div className="field confirm-dialog-field">
            <label htmlFor={`confirm-phrase-${request.id}`}>
              Type <strong>{phrase}</strong> to confirm
            </label>
            <input
              ref={inputRef}
              id={`confirm-phrase-${request.id}`}
              type="text"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              placeholder={phrase}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                submit();
              }}
            />
          </div>
        ) : null}
        {request.kind === "prompt" ? (
          <div className="field confirm-dialog-field">
            <label htmlFor={`confirm-input-${request.id}`}>{request.options.label}</label>
            <input
              ref={inputRef}
              id={`confirm-input-${request.id}`}
              type="text"
              defaultValue={request.options.defaultValue ?? ""}
              placeholder={request.options.placeholder}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                submit();
              }}
            />
          </div>
        ) : null}
        <div className="confirm-dialog-actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn-ghost"
            onClick={() => settleCurrent(cancelValue)}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={danger ? "btn btn-danger" : "btn btn-primary"}
            onClick={submit}
            disabled={!phraseSatisfied}
          >
            {confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
